/**
 * TaskAssignmentFields Component
 *
 * Request Details section:
 * - Team / Assignee (team-based assignment, PLATFORM without a linked project)
 * - OPTIONALS: Linked Project (+ its assignee) and Urgency
 *
 * The Channel Partner / Advertiser picker lives in the Task Details section now.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Card, Col, Row, Form, Select, Divider, Badge, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import { startCase } from 'lodash';

import { urgencyTypes, urgencyColors } from '../../configs/task.config';
import { compareOptionLabels, deduplicateUsers, matchesOptionLabel } from '../../utils/task.utils';
import { filterEnabledUsers } from '../../../../../core/utils/enabledUserFilter';
import '../../styles/index.scss';

const { Option, OptGroup } = Select;

const TaskAssignmentFields = ({
  form,
  level,
  currentTaskId,
  currentTask,
  isEditMode,
  selectedProjectId,
  userOrgId,
  setShowCollaboratorModal,
  setSelectedCollaboratorId,
  setShowFollowerModal,
  setSelectedFollowerId,
  teams,
  teamsLoading,
  selectedTeamId,
  setSelectedTeamId,
  fetchTeamsByOrg,
  linkedProjectId,
  setSelectedProjectId,
  projects,
  projectAssigneeGroups,
  setSelectedAdditionalProjectIds,
  fetchProjects,
  assigneeUsers,
  fetchBasicUsers,
  basicUsersDetailsLoading,
}) => {
  // Assignee options: all fetched users by default (so an existing/legacy assignee is shown),
  // narrowed to the selected team's members once a team is picked. The current assignee(s) are
  // always included so they stay visible/selectable even if outside the fetched/team list.
  // Team members aren't server-filtered like the fetched user list, so drop disabled ones here.
  const teamMembers = filterEnabledUsers(
    (teams || []).find((t) => t.id === selectedTeamId)?.members
  );
  const baseAssigneeUsers = selectedTeamId ? teamMembers : assigneeUsers || [];
  const existingAssignees = currentTask?.assigneeUsers || [];
  // basicUsersDetails can contain the same user more than once (a user matches via multiple
  // roles), so dedupe — same as the board's assignee filter — to avoid duplicate names.
  const filteredAssigneeUsers = deduplicateUsers(
    baseAssigneeUsers.concat(
      existingAssignees.filter((u) => !baseAssigneeUsers.some((bu) => bu.id === u.id))
    )
  );

  // Anyone already assigned but on none of the task's projects still has to be renderable,
  // or the select shows a raw id instead of their name.
  const groupedMemberIds = new Set(
    projectAssigneeGroups.flatMap((group) => group.members.map((member) => member.id))
  );
  const unlistedAssignees = existingAssignees.filter((user) => !groupedMemberIds.has(user.id));
  const assigneeGroups = unlistedAssignees.length
    ? [...projectAssigneeGroups, { id: 'assigned', name: 'Already assigned', members: unlistedAssignees }]
    : projectAssigneeGroups;

  return (
    <Col className="task-assignment-fields">
      <Card
        className="task-assignment-fields__card"
        title={<Text className="task-assignment-fields__card-title">REQUEST DETAILS</Text>}
      >
        {/* Team and Assignee (Platform only, no linked project) */}
        {level === 'PLATFORM' && !selectedProjectId && (
          <Row gutter={32}>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item
                name="teamId"
                label={
                  <div>
                    <div>
                      Team
                      {isEditMode && (
                        <Tooltip title="Changing the team adds the new team's subscribers as collaborators and keeps everyone already on this task — existing collaborators are not removed.">
                          <InfoCircleOutlined style={{ marginLeft: 6, color: '#faad14', cursor: 'help' }} />
                        </Tooltip>
                      )}
                    </div>
                    <div className="task-assignment-fields__label-hint">
                      Assign this task to a team
                    </div>
                  </div>
                }
                colon={false}
              >
                <Select
                  allowClear
                  showSearch
                  placeholder="Select Team"
                  loading={teamsLoading}
                  onFocus={() => userOrgId && fetchTeamsByOrg(userOrgId)}
                  onSearch={(searchText) => userOrgId && fetchTeamsByOrg(userOrgId, searchText)}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  onChange={(value) => {
                    setSelectedTeamId(value || null);
                    form.setFieldsValue({ assigneeUserId: [] });
                  }}
                  className="custom-select"
                >
                  {(teams || []).map((team) => (
                    <Option key={team.id} value={team.id}>
                      {team.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item
                name="assigneeUserId"
                label={
                  <div>
                    <div>Assignee</div>
                    <div className="task-assignment-fields__label-hint">
                      Optional: pick any user, or select a team to narrow to its members
                    </div>
                  </div>
                }
                colon={false}
              >
                <Select
                  placeholder="Select Assignee"
                  showSearch
                  allowClear
                  loading={basicUsersDetailsLoading}
                  onFocus={() => fetchBasicUsers()}
                  onSearch={(value) => fetchBasicUsers(value)}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  filterSort={(optionA, optionB) =>
                    optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase())
                  }
                  onChange={(value) => {
                    const collaborators = currentTask?.collaborators || [];
                    const collaboratorIds = collaborators.map((user) => user.id);
                    const followers = currentTask?.followers || [];
                    const followerIds = followers.map((user) => user.id);
                    if (currentTaskId && collaboratorIds.includes(value)) {
                      setShowCollaboratorModal(true);
                      setSelectedCollaboratorId(value);
                    } else if (currentTaskId && followerIds.includes(value)) {
                      setShowFollowerModal(true);
                      setSelectedFollowerId(value);
                    } else {
                      form.setFieldsValue({ assigneeUserId: value });
                    }
                  }}
                  className="custom-select"
                >
                  {filteredAssigneeUsers.map((user) => (
                    <Option key={user.id} value={user.id}>
                      {startCase(user.name || user.username)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        )}

        {/* OPTIONALS */}
        <Divider orientation="left" className="task-assignment-fields__optionals-divider">
          OPTIONALS
        </Divider>

        <Row gutter={32}>
          {level === 'PLATFORM' && (
            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item
                name="linkedProjectId"
                label={
                  <div>
                    <div>Linked Project</div>
                    <div className="task-assignment-fields__label-hint">
                      Optional: Link this task to a project
                    </div>
                  </div>
                }
                colon={false}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Select a project"
                  disabled={linkedProjectId && currentTaskId === 'new'}
                  onFocus={() => fetchProjects()}
                  onSearch={(searchText) => fetchProjects(searchText)}
                  onChange={(value) => {
                    setSelectedProjectId(value);
                    form.setFieldsValue({ assigneeUserId: [] });
                    const additional = form.getFieldValue('additionalProjectIds') || [];
                    if (value) {
                      // The primary can't also be listed as an additional project
                      if (additional.includes(value)) {
                        const rest = additional.filter((id) => id !== value);
                        form.setFieldsValue({ additionalProjectIds: rest });
                        setSelectedAdditionalProjectIds(rest);
                      }
                      // A project-linked task is assigned via the project, not a team — clear any
                      // team so the task isn't submitted linked to both (the Team field hides while
                      // a project is selected, but antd preserves its hidden value otherwise).
                      setSelectedTeamId(null);
                      form.setFieldsValue({ teamId: null, dueDate: null });
                    } else if (additional.length) {
                      // Clearing the main project hands the role to the first extra rather than
                      // leaving the task in projects with none of them primary. Visible in the
                      // fields, so it can be changed before saving.
                      const [promoted, ...rest] = additional;
                      form.setFieldsValue({ linkedProjectId: promoted, additionalProjectIds: rest });
                      setSelectedProjectId(promoted);
                      setSelectedAdditionalProjectIds(rest);
                    }
                    form.validateFields(['dueDate']);
                  }}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  className="custom-select"
                >
                  {projects.map((project) => (
                    <Option key={project.id} value={project.id}>
                      {project.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )}
          {level === 'PLATFORM' && (
            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item
                name="additionalProjectIds"
                label={
                  <div>
                    <div>Additional Projects</div>
                    <div className="task-assignment-fields__label-hint">
                      Optional: Also show this task in other projects
                    </div>
                  </div>
                }
                colon={false}
              >
                <Select
                  showSearch
                  allowClear
                  mode="multiple"
                  placeholder="Select other projects"
                  onChange={(value) => {
                    const next = value || [];
                    // With no main project picked yet the first choice becomes it, so the fields
                    // always show the primary that will actually be saved.
                    if (!form.getFieldValue('linkedProjectId') && next.length) {
                      const [promoted, ...rest] = next;
                      form.setFieldsValue({ linkedProjectId: promoted, additionalProjectIds: rest });
                      setSelectedProjectId(promoted);
                      setSelectedAdditionalProjectIds(rest);
                      return;
                    }
                    setSelectedAdditionalProjectIds(next);
                  }}
                  onFocus={() => fetchProjects()}
                  onSearch={(searchText) => fetchProjects(searchText)}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  className="custom-select"
                >
                  {projects
                    .filter((project) => project.id !== selectedProjectId)
                    .map((project) => (
                      <Option key={project.id} value={project.id}>
                        {project.name}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
          )}
          <Col xs={24} sm={24} md={12} lg={12}>
            <Form.Item
              name="urgency"
              label={
                <div>
                  <div>Urgency</div>
                  <div className="task-assignment-fields__label-hint">
                    Optional: Set the urgency or priority level of this task
                  </div>
                </div>
              }
              colon={false}
            >
              <Select placeholder="Select urgency type" className="custom-select">
                {Object.keys(urgencyTypes).map((urgency) => (
                  <Option key={urgency} value={urgencyTypes[urgency]}>
                    <Badge
                      color={urgencyColors[urgencyTypes[urgency]]}
                      className="task-basic-fields__urgency-badge"
                    />
                    {urgency}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Linked-project assignee (project owner + team members) */}
        {level === 'PLATFORM' && selectedProjectId && (
          <Row gutter={32}>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item
                name="assigneeUserId"
                label={
                  <div>
                    <div>Assignee</div>
                    <div className="task-assignment-fields__label-hint">
                      Select from project owner and team members
                    </div>
                  </div>
                }
                colon={false}
              >
                <Select
                  placeholder="Select Assignee"
                  showSearch
                  filterOption={matchesOptionLabel}
                  filterSort={compareOptionLabels}
                  className="custom-select"
                >
                  {assigneeGroups.map((group) => (
                    <OptGroup key={group.id} label={group.name}>
                      {group.members.map((user) => (
                        <Option key={`${group.id}-${user.id}`} value={user.id}>
                          {startCase(user.name || user.username)}
                        </Option>
                      ))}
                    </OptGroup>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        )}
      </Card>
    </Col>
  );
};

TaskAssignmentFields.propTypes = {
  form: PropTypes.object.isRequired,
  level: PropTypes.string.isRequired,
  currentTaskId: PropTypes.string,
  currentTask: PropTypes.object,
  isEditMode: PropTypes.bool,
  selectedProjectId: PropTypes.string,
  userOrgId: PropTypes.string,
  setShowCollaboratorModal: PropTypes.func.isRequired,
  setSelectedCollaboratorId: PropTypes.func.isRequired,
  setShowFollowerModal: PropTypes.func.isRequired,
  setSelectedFollowerId: PropTypes.func.isRequired,
  teams: PropTypes.array,
  teamsLoading: PropTypes.bool,
  selectedTeamId: PropTypes.string,
  setSelectedTeamId: PropTypes.func,
  fetchTeamsByOrg: PropTypes.func,
  linkedProjectId: PropTypes.string,
  setSelectedProjectId: PropTypes.func,
  projects: PropTypes.array,
  projectAssigneeGroups: PropTypes.array,
  setSelectedAdditionalProjectIds: PropTypes.func,
  fetchProjects: PropTypes.func,
  assigneeUsers: PropTypes.array,
  fetchBasicUsers: PropTypes.func,
  basicUsersDetailsLoading: PropTypes.bool,
};

TaskAssignmentFields.defaultProps = {
  currentTaskId: null,
  currentTask: null,
  isEditMode: false,
  selectedProjectId: null,
  userOrgId: null,
  teams: [],
  teamsLoading: false,
  selectedTeamId: null,
  setSelectedTeamId: () => {},
  fetchTeamsByOrg: () => {},
  linkedProjectId: null,
  setSelectedProjectId: () => {},
  projects: [],
  projectAssigneeGroups: [],
  setSelectedAdditionalProjectIds: () => {},
  fetchProjects: () => {},
  assigneeUsers: [],
  fetchBasicUsers: () => {},
  basicUsersDetailsLoading: false,
};

export default TaskAssignmentFields;
